import { isAxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Layout } from "../components/Layout";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";
import { Toast } from "../services/toast-service";
import PushNotificationForm from "./PushNotification/PushNotificationForm";
import PushNotificationFormThankYou from "./PushNotification/PushNotificationForm.ThankYou";
import { PushNotificationFormValues } from "./PushNotification/PushNotificationForm.Types";

interface PushNotificationProps {
  userInfo: UserInfo;
}

const PushNotification = ({ userInfo }: PushNotificationProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const { handleSubmit, watch, control, setValue, reset } =
    useForm<PushNotificationFormValues>({
      defaultValues: {
        notificationType: "",
        body: "",
        title: "",
        sendPushNotification: false,
        shortTermAnnouncement: false,
        allRecipients: true,
        sendAsap: true,
      },
    });
  const formValues = watch();
  const selectedNotificationType = watch("notificationType");

  const onSubmit = async () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      setLoading(true);
      try {
        await notificationService.createNotification({
          title: formValues.title,
          body: formValues.body,
          notificationType: formValues.notificationType,
          sendAt: formValues.sendAt
            ? new Date(formValues.sendAt).toISOString()
            : new Date().toISOString(),
          link: formValues.url,
          buttonLabel: formValues.buttonLabel,
          recipientsAll: formValues.allRecipients,
          recipientsCSV: formValues.recipientsCSVFile as File,
          pushNotification: true,
          shortTermAnnouncementExpiryDate:
            formValues.shortTermAnnouncementExpiryDate
              ? new Date(
                  formValues.shortTermAnnouncementExpiryDate,
                ).toISOString()
              : undefined,
          sendPushNotification: formValues.sendPushNotification,
          shortTermAnnouncement: formValues.shortTermAnnouncement,
          emailNotification: false,
        });
        setShowThankYou(true);
      } catch (err: unknown) {
        console.error("onSubmit error:", err);

        let message = `Something went wrong. Please try again. ${err}`;
        if (isAxiosError(err)) {
          if (err.response?.data?.message) {
            message = err.response.data.message;
          } else if (err.response?.status === 400) {
            message = "Bad request. Please check the input fields.";
          } else if (err.response?.status === 401) {
            message = "Unauthorized. Please log in again.";
          } else if (err.response?.status === 403) {
            message =
              "The content provided was invalid. Please review and try again.";
          } else if (err.response?.status === 500) {
            message = "Server error. Please try again later.";
          }
        }

        Toast("Error!", message, "danger");
      }
    }
    setLoading(false);
  };

  return (
    <Layout userInfo={userInfo} loading={loading} hideNavigationLinks>
      {showThankYou ? (
        <PushNotificationFormThankYou
          setCurrentStep={setCurrentStep}
          setShowThankYou={setShowThankYou}
          reset={reset}
          formValues={formValues}
        />
      ) : (
        <PushNotificationForm
          currentStep={currentStep}
          setLoading={setLoading}
          setShowThankYou={setShowThankYou}
          userInfo={userInfo}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          control={control}
          setValue={setValue}
          formValues={formValues}
          notificationType={selectedNotificationType}
          setCurrentStep={setCurrentStep}
        />
      )}
    </Layout>
  );
};

export default PushNotification;
